import { Button, Stack, Text } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link as RouterLink, useNavigate } from 'react-router'
import { api, isApiError } from '~/api/client'
import { keys } from '~/api/hooks'
import { ActionBar } from '~/components/member/ActionBar'
import { ErrorAlert, useFriendlyError } from '~/components/member/feedback'
import { ArtHeading } from '~/components/member/Illustration'
import { MemberLayout } from '~/components/member/layout'
import { useT } from '~/i18n/use-t'
import { useHashParams } from '~/lib/hash'
import { dropPrivateSession } from '~/lib/session'

/**
 * The page behind the sign-in email's button (`/login/link#t=…`, passwordless spec §3, §6). The token stays in the
 * fragment and is posted only when "Sign in" is pressed (P1: a mail scanner that opens the page, even one that runs
 * scripts, never uses the link up). Signed in: the voter page. A used, old or replaced link says so and offers a new
 * email (the sign-in page).
 */
export default function LoginLink() {
  const { t } = useT()
  const friendly = useFriendlyError()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const params = useHashParams()
  const token = params.get('t') ?? params.get('token')

  const signIn = useMutation({
    mutationFn: () => api('memberSignInLink', { body: { token: token! } }),
    onSuccess: (me) => {
      dropPrivateSession(qc)
      qc.setQueryData(keys.me, me)
      navigate('/me', { replace: true })
    },
  })

  const badLink = !token || (signIn.isError && isApiError(signIn.error) && signIn.error.status < 500)
  return (
    <MemberLayout>
      <Stack gap={6}>
        <ArtHeading title={t('loginLink.title')} art='email' />
        {badLink ? (
          <>
            <Text role='alert'>{t('loginLink.oldLink')}</Text>
            <ActionBar>
              <Button asChild size='lg' w='full'>
                <RouterLink to='/login'>{t('loginLink.newEmail')}</RouterLink>
              </Button>
            </ActionBar>
          </>
        ) : (
          <>
            <Text>{t('loginLink.text')}</Text>
            {signIn.isError && <ErrorAlert>{friendly(signIn.error)}</ErrorAlert>}
            <ActionBar>
              <Button size='lg' w='full' loading={signIn.isPending} onClick={() => signIn.mutate()}>
                {t('loginLink.button')}
              </Button>
            </ActionBar>
          </>
        )}
      </Stack>
    </MemberLayout>
  )
}
